import { useCallback, useEffect, useRef, useState } from 'react';
import type { CodexAccount, CodexAuthBlob, MeResponse, SummaryLang } from '../../shared/api';
import { getMe } from './api';
import {
  HISTORY_LIMIT,
  STORAGE_KEYS,
  clearCodexLogin,
  loadCodexLogin,
  loadHistory,
  loadSettings,
  saveCodexLogin,
  saveHistory,
  saveSettings,
  type HistoryEntry,
  type Settings,
  type StoredCodexLogin,
} from './storage';

const ANONYMOUS: MeResponse = { user: null, codex: { stored: false } };

/** GET /api/me with a promise to await the current request (used before starting a summary). */
export function useMe() {
  const [me, setMe] = useState<MeResponse | null>(null);
  /**
   * The request failed (offline, server error) before any answer was known: `me` is a stand-in
   * (anonymous), not a confirmed sign-out. Account data such as the tab cache must stay untouched.
   */
  const [unconfirmed, setUnconfirmed] = useState(false);
  const known = useRef<MeResponse | null>(null);
  const pending = useRef<Promise<MeResponse> | null>(null);

  const refresh = useCallback((): Promise<MeResponse> => {
    const promise: Promise<MeResponse> = getMe().then(
      (value) => {
        if (pending.current === promise) {
          known.current = value;
          setMe(value);
          setUnconfirmed(false);
        }
        return value;
      },
      () => {
        // Keep the last known answer; without one, act anonymously until a retry succeeds.
        const fallback = known.current ?? ANONYMOUS;
        if (pending.current === promise) {
          setMe(fallback);
          setUnconfirmed(!known.current);
        }
        return fallback;
      },
    );
    pending.current = promise;
    return promise;
  }, []);

  useEffect(() => {
    if (!pending.current) void refresh();
  }, [refresh]);

  // Retry a failed first request when the connection or the app comes back.
  useEffect(() => {
    if (!unconfirmed) return;
    const retry = () => void refresh();
    const timer = window.setInterval(retry, 30_000);
    window.addEventListener('online', retry);
    window.addEventListener('focus', retry);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('online', retry);
      window.removeEventListener('focus', retry);
    };
  }, [unconfirmed, refresh]);

  const whenReady = useCallback((): Promise<MeResponse> => pending.current ?? refresh(), [refresh]);

  return { me, unconfirmed, refresh, whenReady };
}

/** Re-reads a localStorage-backed value when another tab changes it. */
function useStorageSync(key: string, reload: () => void) {
  const reloadRef = useRef(reload);
  useEffect(() => {
    reloadRef.current = reload;
  });
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === key || event.key === null) reloadRef.current();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [key]);
}

export function useSettings() {
  const [settings, setSettings] = useState<Settings>(loadSettings);
  useStorageSync(STORAGE_KEYS.settings, () => setSettings(loadSettings()));
  const update = useCallback((patch: Partial<Settings>) => {
    setSettings((previous) => {
      const next = { ...previous, ...patch };
      saveSettings(next);
      return next;
    });
  }, []);
  return [settings, update] as const;
}

/** Browser-stored Codex login (anonymous users, or signed-in users who declined to move it). */
export function useLocalCodexLogin() {
  const [login, setLogin] = useState<StoredCodexLogin | null>(loadCodexLogin);
  useStorageSync(STORAGE_KEYS.codex, () => setLogin(loadCodexLogin()));
  const save = useCallback((auth: CodexAuthBlob, account?: CodexAccount) => setLogin(saveCodexLogin(auth, account)), []);
  const clear = useCallback(() => {
    clearCodexLogin();
    setLogin(null);
  }, []);
  return { login, save, clear };
}

export function useHistory() {
  const [entries, setEntries] = useState<HistoryEntry[]>(loadHistory);
  useStorageSync(STORAGE_KEYS.history, () => setEntries(loadHistory()));

  const upsert = useCallback((entry: HistoryEntry) => {
    setEntries((previous) => {
      const rest = previous.filter((e) => !(e.videoId === entry.videoId && e.lang === entry.lang));
      return saveHistory([entry, ...rest].slice(0, HISTORY_LIMIT));
    });
  }, []);

  const remove = useCallback((videoId: string, lang: SummaryLang) => {
    setEntries((previous) => saveHistory(previous.filter((e) => !(e.videoId === videoId && e.lang === lang))));
  }, []);

  const restore = useCallback((list: HistoryEntry[]) => setEntries(saveHistory(list)), []);

  return { entries, upsert, remove, restore };
}

/** Current time, updated every `intervalMs` while `active`. */
export function useNow(active: boolean, intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(timer);
  }, [active, intervalMs]);
  return now;
}
